/** @file FrameRuler: frame columns 0..frames with the playhead; press or drag across it to scrub. */
import type { CSSProperties, ReactElement } from 'react';
import { useEditor } from '../../state/useEditor';
import { useEditorStore } from '../../state/useEditorStore';
import { FRAME_WIDTH } from './frameWidth';
import { mirrorStart } from './mirrorStart';
import { usePlayheadClip } from './usePlayheadClip';

/** Columns at or after `mirrored` are greyed (computed half). */
function cellStyle(frame: number, current: number, mirrored: number): CSSProperties {
  return {
    width: FRAME_WIDTH, flex: 'none', height: 18, fontSize: 9, textAlign: 'center', userSelect: 'none', cursor: 'ew-resize',
    borderLeft: frame % 5 === 0 ? '1px solid var(--border, #2a2e38)' : 'none',
    background: frame === current ? 'var(--accent, #f2b544)' : frame >= mirrored ? 'var(--grey-bg, #23262e)' : 'transparent',
    color: frame === current ? '#15171c' : 'var(--muted, #8b919e)',
  };
}

/** One column per frame of the playhead clip; pointer down jumps (and pauses), moving with the button held scrubs. */
export function FrameRuler(): ReactElement | null {
  const store = useEditorStore();
  const cur = usePlayheadClip();
  const frame = useEditor((s) => s.playhead.frame);
  if (cur === null) return null;
  const mirrored = mirrorStart(cur.clip) ?? Infinity;
  const seek = (f: number): void => { store.getState().setPlayhead({ frame: f, playing: false }); };
  return (
    <div role="slider" aria-label="frame" aria-valuemin={0} aria-valuemax={cur.clip.frames} aria-valuenow={frame}
      style={{ display: 'flex', paddingLeft: 90 }}>
      {Array.from({ length: cur.clip.frames + 1 }, (_, f) => (
        <div key={f} data-ruler-frame={f} style={cellStyle(f, frame, mirrored)}
          onPointerDown={() => { seek(f); }}
          onPointerEnter={(e) => { if ((e.buttons & 1) === 1) seek(f); }}>
          {f % 5 === 0 ? f : ''}
        </div>
      ))}
    </div>
  );
}
